import { Button } from '@lessonup/ui-components';
import React, { SVGProps } from 'react';
import { colorPickerPlaceholderColor } from './ColorpickerModal.utils';

interface EyeDropperProps {
  handleOnChange: (color: string) => void;
  hide: () => void;
  show: () => void;
  selectedColor?: string;
}

export const EyeDropTool: React.FC<EyeDropperProps> = ({ handleOnChange, selectedColor, hide, show }) => {
  const eyeDropper = getEyeDropper();
  if (!eyeDropper) {
    return null;
  }

  const handleClick = async () => {
    try {
      hide();
      const result = await eyeDropper.open();
      show();
      handleOnChange(result.sRGBHex);
    } catch (error) {
      show();
      console.error('Error picking color:', error);
    }
  };

  return (
    <div>
      <Button
        size="large"
        buttonType="neutral"
        showStroke
        iconStart={<SvgIconColorPicker dropperColor={selectedColor} />}
        onClick={handleClick}
      ></Button>
    </div>
  );
};

function getEyeDropper(): EyeDropper | undefined {
  const win = window as WindowPolyfill;
  if (typeof win === 'undefined' || !win.EyeDropper) {
    return;
  }
  return new win.EyeDropper();
}

/**
 * Types for the EyeDropper API, which is not yet in the DOM lib
 * https://developer.mozilla.org/en-US/docs/Web/API/EyeDropper_API
 * taken from StackOverflow
 */
interface EyeDropper {
  open: (options?: ColorSelectionOptions) => Promise<ColorSelectionResult>;
}

interface EyeDropperConstructor {
  new (): EyeDropper;
}

interface ColorSelectionOptions {
  signal?: AbortSignal;
}

interface ColorSelectionResult {
  sRGBHex: string;
}

interface WindowPolyfill {
  EyeDropper?: EyeDropperConstructor | undefined;
}

interface SvgIconColorPickerProps extends SVGProps<SVGSVGElement> {
  dropperColor?: string;
}

// Clone of the IconColorPicker component from the UI library with the dropperColor prop added
const SvgIconColorPicker = ({ dropperColor, ...props }: SvgIconColorPickerProps) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} fill="none" viewBox="0 0 24 24" {...props}>
      <path
        fill={dropperColor || colorPickerPlaceholderColor}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity={0.125}
        d="M14.91 14.521 9.48 9.09s-1.817 1.153-3.206 2.543c-.695.695-1.33 1.496-1.793 2.124-.51.692-.748 1.538-.745 2.398l.002.588c.001.474-.186.928-.52 1.263a1.777 1.777 0 0 0 0 2.514l.264.265c.694.694 1.82.694 2.514 0 .334-.335.789-.522 1.262-.52l.608.001c.847.003 1.682-.229 2.371-.722.655-.469 1.493-1.114 2.164-1.785 1.303-1.303 2.51-3.237 2.51-3.237"
      />
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M14.91 14.521 9.48 9.09s-1.817 1.153-3.206 2.543c-.695.695-1.33 1.497-1.793 2.124-.51.692-.748 1.538-.745 2.398l.002.588c.001.474-.186.928-.52 1.263a1.777 1.777 0 0 0 0 2.514l.264.265c.694.694 1.82.694 2.514 0 .334-.335.789-.522 1.262-.52l.608.001c.847.003 1.682-.229 2.371-.722.655-.469 1.493-1.114 2.164-1.784 1.303-1.305 2.51-3.238 2.51-3.238"
      />
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19.865 11.084a4.913 4.913 0 1 0-6.949-6.95l-.713.714a.88.88 0 0 1-1.243 0 1.76 1.76 0 0 0-2.487 0l-.187.187c-.79.79-.79 2.071 0 2.861l7.818 7.818c.79.79 2.07.79 2.86 0l.188-.187c.686-.687.686-1.8 0-2.487a.88.88 0 0 1 0-1.243z"
      />
    </svg>
  );
};
export default SvgIconColorPicker;
